import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import { useAppContext } from '@/hooks'
import { useConfirmationModal } from '@/pages/App/components/ConfirmationService'
import { ConfirmationOptions } from '@/pages/App/types'
import { PostProcessTechnicalColumnDto } from '@/pages/User/pages/Settings/pages/TechnicalColumns/types'
import { useLazyGetTaskExecutionUsingGetQuery } from '@/rtkApi'
import { logError } from '@/utils/errorHandling'

import { runningDescription, runningPercent, TaskProgressDetail } from './taskProgress'

export const TASK_MONITOR_INTERVAL = 3000
export const TASK_STATE_ERROR = 'ERROR'
export const TASK_STATE_FINISHED = 'FINISHED'
export const TASK_STATE_CANCELLED = 'CANCELLED'

export interface MonitorTaskCallbacks {
	dontCloseOnSuccess?: boolean
	onError?: (error: Error, detail?: string) => void
	onProgressUpdate?: (
		options: ConfirmationOptions,
	) => Promise<ConfirmationOptions | null>
	onSuccess?: (taskId: number) => Promise<void>
	postProcessTechnicalColumnDto?: PostProcessTechnicalColumnDto
}

const pollingErrorDetails = (taskId: number, error: unknown) => {
	const details: Record<string, unknown> = { taskId }
	if (error && typeof error === 'object') {
		if ('status' in error) details.httpStatus = error.status
		if ('data' in error) details.responseData = error.data
	}
	return details
}

const taskModalOptions = (
	title: string,
	description: string,
	progress: number,
	taskState?: ConfirmationOptions['taskState'],
): ConfirmationOptions => ({
	description,
	isAsync: true,
	preserveProcessingState: true,
	progress,
	taskState,
	title,
})

export const useTaskMonitor = () => {
	const { t } = useAppContext()
	const { openConfirmModal, closeConfirmModal } = useConfirmationModal()
	const [getTaskExecution] = useLazyGetTaskExecutionUsingGetQuery()
	const intervalRef = useRef<NodeJS.Timeout | null>(null)
	const isCancelledRef = useRef(false)

	const clearMonitoringInterval = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}
	}, [])

	const stopMonitoring = useCallback(() => {
		clearMonitoringInterval()
		isCancelledRef.current = true
	}, [clearMonitoringInterval])

	useEffect(() => clearMonitoringInterval, [clearMonitoringInterval])

	const monitorTask = useCallback(
		async (taskId: number, callbacks?: MonitorTaskCallbacks): Promise<void> => {
			if (!taskId) {
				toast(t('TASK_ERROR'), { type: 'error' })
				return
			}

			const {
				dontCloseOnSuccess,
				onError,
				onProgressUpdate,
				onSuccess,
				postProcessTechnicalColumnDto,
			} = callbacks || {}

			isCancelledRef.current = false
			clearMonitoringInterval()

			const runningOptions = (
				progress: number,
				taskState: ConfirmationOptions['taskState'],
				detail?: TaskProgressDetail,
			) =>
				taskModalOptions(
					t('PROCESSING_TASK').toUpperCase(),
					runningDescription(t('TASK_RUNNING_WAIT'), detail),
					progress,
					taskState,
				)

			const finishedOptions = (taskState?: ConfirmationOptions['taskState']) =>
				taskModalOptions(
					t('TASK_FINISHED').toUpperCase(),
					t('TASK_FINISHED_DESC'),
					100,
					taskState,
				)

			return new Promise((resolve, reject) => {
				const stopAndResolve = () => {
					stopMonitoring()
					resolve()
				}

				const rejectTask = (
					error: Error,
					operation: string,
					details: Record<string, unknown>,
					detail?: string,
				) => {
					stopMonitoring()
					logError(error, { details, operation })
					onError?.(error, detail)
					reject(error)
				}

				intervalRef.current = setInterval(async () => {
					if (isCancelledRef.current) {
						stopAndResolve()
						return
					}

					try {
						const progressResponse = await getTaskExecution({ id: taskId }).unwrap()

						if (isCancelledRef.current) {
							stopAndResolve()
							return
						}

						if (!progressResponse) {
							rejectTask(new Error('Invalid response structure'), 'taskMonitor.getProgress', {
								taskId,
							})
							return
						}

						const progress = runningPercent(
							progressResponse.progress,
							progressResponse.progressDetail,
						)
						const state = progressResponse.state

						if (state === TASK_STATE_ERROR || state === TASK_STATE_CANCELLED) {
							const cancelled = state === TASK_STATE_CANCELLED
							const detail = progressResponse.errorMessage
							const description = cancelled
								? t('TASK_CANCELLED')
								: detail || t('TASK_EXECUTION_FAILED')
							const error = new Error(description)
							stopMonitoring()
							logError(error, {
								details: {
									errorMessage: detail,
									state,
									taskId,
								},
								operation: 'taskMonitor.taskFailed',
							})
							if (!onProgressUpdate && !onError) {
								openConfirmModal({
									...taskModalOptions(
										(cancelled
											? t('TASK_CANCELLED')
											: t('TASK_EXECUTION_FAILED')
										).toUpperCase(),
										description,
										progress,
										state,
									),
									hasError: true,
								})
							}
							onError?.(error, detail)
							postProcessTechnicalColumnDto?.(null)
							reject(error)
							return
						}

						if (state === TASK_STATE_FINISHED) {
							stopMonitoring()
							try {
								if (onProgressUpdate) {
									await onProgressUpdate(finishedOptions(state))
								} else if (dontCloseOnSuccess) {
									await openConfirmModal(finishedOptions(state))
									postProcessTechnicalColumnDto?.(null)
								} else {
									openConfirmModal(finishedOptions(state))
								}

								await onSuccess?.(taskId)

								if (!dontCloseOnSuccess && !onSuccess) {
									closeConfirmModal()
								}

								resolve()
							} catch (error) {
								logError(error, {
									details: { taskId },
									operation: 'taskMonitor.onSuccess',
								})
								onError?.(error as Error)
								reject(error)
							}
							return
						}

						try {
							const options = runningOptions(progress, state, progressResponse.progressDetail)
							if (onProgressUpdate) {
								await onProgressUpdate(options)
							} else {
								openConfirmModal(options)
							}
						} catch (error) {
							logError(error, {
								details: { progress, taskId },
								operation: 'taskMonitor.progressUpdate',
							})
							stopAndResolve()
						}
					} catch (error) {
						if (isCancelledRef.current) {
							stopAndResolve()
							return
						}

						rejectTask(error as Error, 'taskMonitor.polling', pollingErrorDetails(taskId, error))
					}
				}, TASK_MONITOR_INTERVAL)
			})
		},
		[
			clearMonitoringInterval,
			closeConfirmModal,
			getTaskExecution,
			openConfirmModal,
			stopMonitoring,
			t,
		],
	)

	return { monitorTask, stopMonitoring }
}
